import { Fragment, useState, useEffect, useRef } from "react";
import { Dialog, Transition } from "@headlessui/react";
import { PlusCircleIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";
import { useFormik } from "formik";
import * as Yup from "yup";
import IconButton from "../iconButton";
import { convertShortCodes, ehrTags } from "../../utilities/utils";
import EmailTemplatePreview from "./EmailTemplatePreview";
import { useGetUserDetailsQuery } from "../../store/admin/professionalUserInfo";
import { useAdminSaveProfessionalTemplatesMutation, useAdminUpdateProfessionalTemplatesMutation } from "../../store/admin/adminTemplate";

const CreateAdminSmsSlideOver = ({
  show,
  setShowSlide,
  title,
  description,
  templateId,
  handleSuccess,
  smsTemplateName,
  smsTemplateContent,
  professionalId
}) => {
  const textareaRef = useRef(null);
  const [cursorPos, setCursorPos] = useState(0);
  const [selectedTags, setSelectedTags] = useState([]);
  const [templateName, setTemplateName] = useState(smsTemplateName);
  const [templateContent, setTemplateContent] = useState(smsTemplateContent);
  const [showSmsPreview, setShowSmsPreview] = useState(false);
  const [characterCount, setCharacterCount] = useState(
    smsTemplateContent?.length || 0
  );

  const { data: professionalInfo, refetch: professionalInfoDetail } =
  useGetUserDetailsQuery(professionalId);

  useEffect(() => {
    formik.setFieldValue("templateTitle", smsTemplateName);
    setTemplateName(smsTemplateName);
    formik.setFieldValue("templateContent", smsTemplateContent);
    setTemplateContent(smsTemplateContent);
  }, [smsTemplateName, smsTemplateContent]);

  const [
    updateTemplate,
    {
      isSuccess: updateTemplateSuccess,
      isLoading: updateTemplateLoader,
      isError: updateTemplateError,
      data: updateTemplateData,
    },
  ] = useAdminUpdateProfessionalTemplatesMutation();

  const [
    saveTemplate,
    {
      isSuccess: saveTemplateSuccess,
      isLoading: saveTemplateLoader,
      isError: saveTemplateError,
      data: saveTemplateData,
    },
  ] = useAdminSaveProfessionalTemplatesMutation();

  const validationSchema = Yup.object().shape({
    templateTitle: Yup.string().required("Message title is required."),
    templateContent: Yup.string().required("Message content is required."),
  });

  const formik = useFormik({
    initialValues: {
      templateTitle: templateName ?? "",
      templateContent: templateContent ?? "",
    },
    validationSchema,
    onSubmit: (values) => {
      if (formik.isValid) {
        if (title === "Edit") {
          const params = {
            _method: "PUT",
            name: templateName,
            content: templateContent,
            id: templateId,
          };
          updateTemplate(params);
        } else if (title === "Create") {
          const params = {
            type: "sms",
            name: templateName,
            content: templateContent,
          };
          saveTemplate(params);
        }
      }
    },
  });

  useEffect(() => {
    if (updateTemplateSuccess && !updateTemplateLoader) {
      handleSuccess();
      setShowSlide(false);
      toast.success(updateTemplateData.message);
      professionalInfoDetail(professionalId)
    }
    if (saveTemplateSuccess && !saveTemplateLoader) {
      handleSuccess();
      setShowSlide(false);
      toast.success(saveTemplateData.message);
      professionalInfoDetail(professionalId)
    }
  }, [updateTemplateSuccess, saveTemplateSuccess]);

  useEffect(() => {
    // Update character count when content changes
    setCharacterCount(templateContent?.length);
  }, [templateContent]);

  const handleTemplateNameChange = (e) => {
    formik.handleChange(e);
    setTemplateName(e.target.value);
  };

  const handleTemplateContentChange = (e) => {
    formik.handleChange(e);
    const newContent = e.target.value;
    if (newContent?.length <= 300) {
      setTemplateContent(newContent);
    }
  };

  const handleTagClick = (tag) => {
    let tagName;

    if (tag === "+appt schedule link") {
      tagName = "schedule_link";
    } else {
      tagName = tag.toLowerCase().replace(/\s+/g, "_").replace(/^\+/, ""); // Remove the '+' symbol from the beginning of the tag
    }

    const tagWithBraces = `{{${tagName}}}`;
    if (parseInt(templateContent?.length + tagWithBraces?.length) <= 300) {
      const currentCursorPos = textareaRef.current.selectionStart;

      setTemplateContent((prevContent) => {
        const start = prevContent.substring(0, currentCursorPos);
        const end = prevContent.substring(currentCursorPos);
        setCursorPos(currentCursorPos + tagWithBraces?.length);
        return start + tagWithBraces + end;
      });
    }
  };

  useEffect(() => {
    if (textareaRef && textareaRef.current) {
      textareaRef && textareaRef.current?.focus();
      textareaRef &&
        textareaRef.current?.setSelectionRange(cursorPos, cursorPos);

      const lineHeight =
        parseInt(getComputedStyle(textareaRef.current).lineHeight, 10) || 20;
      const linesAboveCursor = cursorPos / textareaRef.current.cols - 5;
      textareaRef.current.scrollTop = lineHeight * linesAboveCursor;
    }
  }, [cursorPos]);

  const handleClose = () => {
    setShowSlide(!show);
  };

  return (
    <>
      <Transition.Root show={show} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={handleClose}>
          <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-hidden">
            <div className="absolute inset-0 overflow-hidden">
              <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full px-[15px] py-5 md:p-0">
                <Transition.Child
                  as={Fragment}
                  enter="transform transition ease-in-out duration-500 sm:duration-700"
                  enterFrom="translate-x-full"
                  enterTo="translate-x-0"
                  leave="transform transition ease-in-out duration-500 sm:duration-700"
                  leaveFrom="translate-x-0"
                  leaveTo="translate-x-full"
                >
                  <Dialog.Panel className="pointer-events-auto w-screen max-w-[478px]">
                    <div className="flex h-full flex-col bg-white py-5 md:pt-10 md:pb-5 px-4 md:px-8 shadow-xl rounded-md md:rounded-0">
                      <div className="">
                        <div className="">
                          <Dialog.Title className="text-xl md:text-2xl font-bold leading-[160%] text-[#04091E] border-b border-solid border-[#CCD0D4] pb-3 md:pb-5 mb-4 md:mb-5">
                            {title} InstantEngage™ SMS Template
                          </Dialog.Title>
                        </div>
                      </div>
                      <div className="relative">
                        <p className="text-sm md:text-base leading-[160%] text-[#485868] mb-5 md:mb-[30px]">
                          {description}
                        </p>
                      </div>
                      <form
                        className="scroller mb-4 md:mb-[30px] max-h-[622px] overflow-auto pr-1"
                        onSubmit={formik.handleSubmit}
                      >
                        <div>
                          <div className="flex justify-between items-center mb-3">
                            <label className="inline-block text-sm md:text-lg font-medium leading-[160%] text-[#485868]">
                              Template Name
                            </label>
                            {/* <div className="text-primary font-black text-[16px] leading-[160%] underline cursor-pointer relative tooltip-icon">
                            Default Template
                            <div className="tooltip absolute right-1 -bottom-[50px] bg-[#191549] rounded-md p-[10px] w-[312px] z-[2] invisible">
                              <div className="w-4 h-4 rotate-45 rounded-tl-[4px] bg-[#191549] -mt-4 mr-[14px] ml-auto"></div>
                              <p className="text-xs font-medium leading-[160%] text-[#F1F4F6]">
                                You can set back to default template by
                                HelpMatch.
                              </p>
                            </div>
                          </div> */}
                          </div>
                          <input
                            type="text"
                            name="templateTitle"
                            placeholder="Template Name"
                            className="text-sm md:text-lg font-bold leading-[160%] text-customGray p-5 h-12 md:h-[70px] border border-solid border-[#CCD0D4] rounded-[10px] mb-[14px] md:mb-[22px] placeholder:text-lg placeholder:font-medium"
                            value={templateName}
                            onChange={handleTemplateNameChange}
                          />
                          {formik.errors.templateTitle &&
                            formik.touched.templateTitle && (
                              <div className="text-red-500 text-sm font-semibold -mt-3 mb-5">
                                {formik.errors.templateTitle}
                              </div>
                            )}
                        </div>
                        <div>
                          <div className="flex justify-between items-center mb-2">
                            <label className="inline-block text-sm md:text-lg font-bold leading-[160%] text-[#485868]">
                              Message{" "}
                              <span className="text-[#EF2121]">
                                ({characterCount}/300)
                              </span>
                            </label>
                            <IconButton
                              classes="rounded-[10px] bg-primary uppercase text-sm font-black uppercase text-white flex justify-center items-center px-5 py-2"
                              iconClasses="w-6 h-6 text-[#485868]"
                              onClick={() => {
                                setShowSmsPreview(true);
                              }}
                              title="preview"
                            />
                          </div>
                          <textarea
                            name="templateContent"
                            ref={textareaRef}
                            className="block w-full text-lg font-medium leading-[160%] text-customGray p-5 h-[160px] md:h-[200px] border border-solid border-strokeColor rounded-[10px] resize-none outline-none mb-4 md:mb-[22px] placeholder:text-lg placeholder:font-medium"
                            placeholder="Hi {{match_firstname}}, this is {{my_name}} reaching out regarding your request to connect..."
                            value={templateContent}
                            onChange={handleTemplateContentChange}
                          ></textarea>
                          {formik.errors.templateContent &&
                            formik.touched.templateContent && (
                              <div className="text-red-500 text-sm font-semibold -mt-3 mb-5">
                                {formik.errors.templateContent}
                              </div>
                            )}
                        </div>
                        <div className="flex flex-col md:flex-row gap-2 md:gap-[22px]">
                          <label className="inline-block text-sm md:text-base font-bold leading-[160%] text-[#485868] py-2">
                            Tags:
                          </label>
                          <div className="flex items-center flex-wrap gap-2.5">
                            {ehrTags.map((tag) => (
                              <span
                                key={tag}
                                className="px-2 bg-[#F1F4F6] border border-solid border-[#EBEFF3] rounded-[40px] text-xs font-black leading-[160%] uppercase text-[#485868] inline-flex items-center justify-center h-8 cursor-pointer"
                                onClick={() => handleTagClick(tag)}
                              >
                                {tag}
                              </span>
                            ))}
                          </div>
                        </div>
                      </form>
                      <div>
                        <IconButton
                          type="submit"
                          title={
                            title === "Edit"
                              ? "Save SMS Template"
                              : `${title} SMS Template`
                          }
                          classes="rounded-[10px] bg-primary uppercase text-sm md:text-base font-black uppercase text-white py-2 md:p-4 px-3 flex justify-center items-center gap-[6px] w-full mb-4"
                          icon={PlusCircleIcon}
                          onClick={formik.handleSubmit}
                          iconClasses="icon w-6 h-6 text-[#ffffff]"
                          buttonLoader={
                            updateTemplateLoader || saveTemplateLoader
                          }
                        />
                        <button
                          type="button"
                          className="rounded-[10px] bg-[#EBEFF3] text-sm md:text-base font-black uppercase text-[#485868] py-2 md:p-4 px-3 flex justify-center items-center gap-[6px] w-full"
                          onClick={() => handleClose()}
                        >
                          CANCEL
                        </button>
                      </div>
                    </div>
                  </Dialog.Panel>
                </Transition.Child>
              </div>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
      {showSmsPreview && (
        <EmailTemplatePreview
          showModal={showSmsPreview}
          setShowModal={setShowSmsPreview}
          content={convertShortCodes(templateContent, professionalInfo?.data)}
        />
      )}
    </>
  );
};

export default CreateAdminSmsSlideOver;
